import { useIntl } from 'react-intl';
import type {
    FindingDocType,
    FindingPlanumDocType,
    FindingProfileDocType
} from '@ast-data/shared';
import {
    labelledListSortComparator,
    type GridColDef
} from '../../lib/dataGrid';
import FindingRefsCell from './FindingRefsCell';
import {
    useFindingRefResolver,
    useRefFindingOptions,
    type RowWithRefs
} from './findingRefLabels';

/**
 * The column those chips fill: a record's Befund references, filterable as a
 * multiSelect over the family's descriptions.
 *
 * Its value is the record's THREE ref arrays merged — a Foto or Fund may point
 * at a whole Befund as readily as at one Planum — so the cell, the filter and
 * the export all read the same set. That, and the options and the chips, is
 * what the four referencing grids had in common; sizing is left to the call
 * site to spread over it.
 */
export function useFindingRefsColumn<T extends RowWithRefs>({
    headerId,
    findings,
    planums,
    profiles
}: {
    /** i18n id of the header — each record names its references its own way. */
    headerId: string;
    findings: FindingDocType[];
    planums: FindingPlanumDocType[];
    profiles: FindingProfileDocType[];
}): GridColDef<T> {
    const intl = useIntl();
    // The family's descriptions as the filter's options ("Bef. 7000, Fl. 1,
    // Pl. 1"), and id → label + route so each chip links to its Befund.
    const options = useRefFindingOptions(findings, planums, profiles);
    const resolver = useFindingRefResolver(findings, planums, profiles);
    return {
        field: 'findingIds',
        type: 'multiSelect',
        headerName: intl.formatMessage({ id: headerId }),
        sortComparator: labelledListSortComparator(
            (id) => resolver.get(id)?.label ?? ''
        ),
        valueOptions: options,
        valueGetter: (_value, row) => [
            ...row.planumIds,
            ...row.profileIds,
            ...(row.findingIds ?? [])
        ],
        renderCell: (params) => (
            <FindingRefsCell
                ids={params.value as string[]}
                resolver={resolver}
            />
        )
    };
}
